// Extra product photos ("More photos") for the storefront hover float.
//
// A product keeps its single main photo in `image_url`; up to
// MAX_GALLERY_IMAGES more live in `gallery_images`, shown in the dish's photo
// strip on the storefront. Each entry is
// sent the way `image_url` is: an existing http(s) URL, or a `data:image/...`
// URI the backend rehosts on Cloudinary.
//
// Cutouts (`image_cutout_url`, `gallery_images[].cutout_url`) are transparent
// versions made by an offline tool. The admin never edits them; the form only
// keeps them attached to the photo they were made from and clears them when
// that photo is replaced.

import type { ProductGalleryImage } from '@/types'

export const MAX_GALLERY_IMAGES = 3

/** The saved gallery as the form's starting state (defensive about null / junk / overflow). */
export function initialGallery(saved: ProductGalleryImage[] | null | undefined): ProductGalleryImage[] {
  if (!Array.isArray(saved)) return []
  return saved
    .filter((g) => g && typeof g.url === 'string' && g.url.trim() !== '')
    .slice(0, MAX_GALLERY_IMAGES)
    .map((g) => (g.cutout_url ? { url: g.url, cutout_url: g.cutout_url } : { url: g.url }))
}

/**
 * The `gallery_images` value the form sends. A new upload is a fresh entry
 * with no cutout, so its stale cutout cannot survive; an unchanged entry
 * (possibly moved) still carries the cutout it was saved with.
 */
export function galleryPayload(draft: ProductGalleryImage[]): ProductGalleryImage[] {
  return draft
    .filter((g) => g.url.trim() !== '')
    .slice(0, MAX_GALLERY_IMAGES)
    .map((g) => (g.cutout_url ? { url: g.url.trim(), cutout_url: g.cutout_url } : { url: g.url.trim() }))
}

/** Order-sensitive identity, for the unsaved-changes check. */
export function gallerySnapshot(draft: ProductGalleryImage[]): string {
  return JSON.stringify(galleryPayload(draft).map((g) => g.url))
}

/**
 * The main photo's cutout field for the payload. Omitted (backend keeps it)
 * unless an existing product's main photo was replaced or removed, in which
 * case it is explicitly cleared. A new product has nothing to clear.
 */
export function mainCutoutPatch(
  isEditing: boolean,
  savedImageUrl: string | null | undefined,
  nextImageUrl: string | null | undefined,
): { image_cutout_url?: null } {
  if (!isEditing) return {}
  const before = (savedImageUrl ?? '').trim()
  const after = (nextImageUrl ?? '').trim()
  return before === after ? {} : { image_cutout_url: null }
}

/** Move one entry left (-1) or right (+1); out-of-range moves are no-ops. */
export function moveGalleryImage<T>(list: T[], index: number, dir: -1 | 1): T[] {
  const to = index + dir
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list
  const next = list.slice()
  ;[next[index], next[to]] = [next[to], next[index]]
  return next
}
